/**
 * MODULE: domain/sales/frontend/components/accounts/QuickAddCardForm.tsx
 * PURPOSE: Inline form for quickly adding org chart cards (Contact, Placeholder, Group)
 * DEPENDENCIES: flowbite-react, react-icons/hi
 * PUBLIC API: QuickAddCardForm (default)
 * SIDE EFFECTS: None
 * AGENT SAFETY: 🟢 SAFE
 * FILE SIZE: ~198 / 220 lines
 * RECENT: 2026-02-15 - Theme (light/dark) compatibility
 */

import React, { useState } from 'react';
import { Button, Label, Select, TextInput } from 'flowbite-react';
import { HiCheck, HiX } from 'react-icons/hi';
import { CardType } from './OrgChartNode';

export interface QuickAddCardFormProps {
  position: { x: number; y: number };
  onSave: (data: {
    firstName: string;
    lastName: string;
    title?: string;
    cardType: CardType;
    groupType?: string;
  }) => void;
  onCancel: () => void;
}

export function QuickAddCardForm({
  position,
  onSave,
  onCancel,
}: QuickAddCardFormProps) {
  const [cardType, setCardType] = useState<CardType>(CardType.PLACEHOLDER);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [title, setTitle] = useState('');
  const [groupType, setGroupType] = useState('department');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim()) return;

    onSave({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      title: title.trim(),
      cardType,
      groupType: cardType === CardType.GROUP ? groupType : undefined,
    });
  };

  return (
    <div
      className="absolute z-50"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: '280px',
      }}
    >
      <form
        onSubmit={handleSubmit}
        className="rounded-lg border-2 border-blue-500 dark:border-blue-400 bg-white dark:bg-gray-800 p-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3">
          <Label htmlFor="cardType" className="mb-2 text-gray-900 dark:text-white">Card Type</Label>
          <Select
            id="cardType"
            value={cardType}
            onChange={(e) => setCardType(e.target.value as CardType)}
            sizing="sm"
            className="border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value={CardType.PLACEHOLDER}>Placeholder (convert later)</option>
            <option value={CardType.GROUP}>Group (dept/team)</option>
            <option value={CardType.CONTACT}>Full Contact</option>
          </Select>
        </div>

        {cardType === CardType.GROUP ? (
          <>
            <div className="mb-3">
              <Label htmlFor="groupName" className="mb-2 text-gray-900 dark:text-white">Group Name</Label>
              <TextInput
                id="groupName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="e.g., Engineering"
                sizing="sm"
                className="border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
                required
                autoFocus
              />
            </div>
            <div className="mb-3">
              <Label htmlFor="groupType" className="mb-2 text-gray-900 dark:text-white">Type</Label>
              <Select
                id="groupType"
                value={groupType}
                onChange={(e) => setGroupType(e.target.value)}
                sizing="sm"
                className="border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="department">Department</option>
                <option value="team">Team</option>
                <option value="division">Division</option>
                <option value="company">Company</option>
              </Select>
            </div>
          </>
        ) : (
          <>
            <div className="mb-3">
              <Label htmlFor="firstName" className="mb-2 text-gray-900 dark:text-white">First Name</Label>
              <TextInput
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="John"
                sizing="sm"
                className="border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
                required
                autoFocus
              />
            </div>
            <div className="mb-3">
              <Label htmlFor="lastName" className="mb-2 text-gray-900 dark:text-white">Last Name</Label>
              <TextInput
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Doe"
                sizing="sm"
                className="border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div className="mb-3">
              <Label htmlFor="title" className="mb-2 text-gray-900 dark:text-white">Job Title</Label>
              <TextInput
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="VP of Sales"
                sizing="sm"
                className="border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </>
        )}

        <div className="flex gap-2">
          <Button type="submit" size="sm" color="blue" className="flex-1">
            <HiCheck className="mr-1 h-4 w-4" />
            Add
          </Button>
          <Button type="button" size="sm" color="gray" onClick={onCancel}>
            <HiX className="h-4 w-4" />
          </Button>
        </div>

        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          {cardType === CardType.PLACEHOLDER &&
            'Hover to convert to full contact later'}
          {cardType === CardType.GROUP && 'Group cards organize structure'}
          {cardType === CardType.CONTACT && 'Will be added to contacts table'}
        </p>
      </form>
    </div>
  );
}

export default QuickAddCardForm;
